import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import StopOutlined from "@mui/icons-material/StopOutlined";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import RecordVoiceOverOutlined from "@mui/icons-material/RecordVoiceOverOutlined";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import SmartphoneOutlined from "@mui/icons-material/SmartphoneOutlined";
import { GlobalContext } from "../../../context/GlobalContext";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Chinese" },
  { value: "hi", label: "Hindi" },
  { value: "ar", label: "Arabic" },
  { value: "", label: "All languages" },
];

const ACCENT_OPTIONS = [
  { value: "", label: "All accents" },
  { value: "american", label: "American" },
  { value: "british", label: "British" },
  { value: "australian", label: "Australian" },
  { value: "irish", label: "Irish" },
  { value: "indian", label: "Indian" },
  { value: "african", label: "African" },
];

const USE_CASE_CHIPS = [
  { id: "advertisement", label: "Advertisement", icon: CampaignOutlined },
  { id: "social_media", label: "Social Media", icon: SmartphoneOutlined },
];

function VoiceRow({ voice, selected, playing, onSelect, onPlay }) {
  const title = voice.name || voice.voice_id || "Voice";
  const desc = voice.description || voice.use_case || voice.category || "";
  return (
    <Box
      onClick={() => onSelect(voice)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        p: 1.25,
        borderRadius: 2,
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected
          ? (t) => alpha(t.palette.primary.main, 0.06)
          : "background.paper",
        cursor: "pointer",
        "&:hover": {
          borderColor: selected ? "primary.main" : "primary.light",
        },
      }}
    >
      <IconButton
        size="small"
        color={playing ? "error" : "primary"}
        disabled={!voice.preview_url}
        onClick={(e) => {
          e.stopPropagation();
          onPlay(voice);
        }}
      >
        {playing ? (
          <StopOutlined fontSize="small" />
        ) : (
          <PlayArrowOutlined fontSize="small" />
        )}
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {title}
        </Typography>
        {desc ? (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {String(desc).slice(0, 80)}
          </Typography>
        ) : null}
      </Box>
      {selected ? <Chip size="small" color="primary" label="Selected" /> : null}
    </Box>
  );
}

/**
 * Pick an ElevenLabs voice (Explore library or My Voices) for Video Studio character dialogue.
 */
export default function CharacterVoicePickerModal({
  open,
  onClose,
  onConfirm,
  lang,
  value,
}) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [tab, setTab] = React.useState("explore");
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [language, setLanguage] = React.useState("en");
  const [accent, setAccent] = React.useState("");
  const [useCase, setUseCase] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [voices, setVoices] = React.useState([]);
  const [selected, setSelected] = React.useState(value || null);
  const [playingId, setPlayingId] = React.useState(null);
  const audioRef = React.useRef(null);

  const stopPreview = React.useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  }, []);

  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    if (!open) {
      stopPreview();
      setSearch("");
      setDebouncedSearch("");
      setLanguage("en");
      setAccent("");
      setUseCase("");
      setTab("explore");
      return undefined;
    }
    setSelected(value || null);
    return () => stopPreview();
  }, [open, stopPreview, value]);

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (tab === "mine") {
          const res = await hitAxios({
            path: "/api/voice-changer/voices",
            post: false,
            admin: false,
            showLoading: false,
            showSnackbar: false,
          });
          if (cancelled) return;
          if (!res?.data?.success) {
            showSnack(res?.data?.msg || "Could not load voices.", false);
            setVoices([]);
            return;
          }
          const all = res.data.data?.voices || [];
          setVoices(
            all.map((v) => ({
              ...v,
              description:
                v.description || v.labels?.description || v.labels?.descriptive || "",
              use_case: v.use_case || v.labels?.use_case || v.category,
            })),
          );
        } else {
          const qs = new URLSearchParams({
            page: "0",
            page_size: "36",
            sort: "trending",
          });
          if (debouncedSearch) qs.set("search", debouncedSearch);
          if (language) qs.set("language", language);
          if (accent) qs.set("accent", accent);
          if (useCase) qs.set("use_cases", useCase);
          const res = await hitAxios({
            path: `/api/voice-changer/shared-voices?${qs.toString()}`,
            post: false,
            admin: false,
            showLoading: false,
            showSnackbar: false,
          });
          if (cancelled) return;
          if (!res?.data?.success) {
            showSnack(res?.data?.msg || "Could not load voices.", false);
            setVoices([]);
            return;
          }
          setVoices(res.data.data?.voices || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accent, debouncedSearch, hitAxios, language, open, showSnack, tab, useCase]);

  const filtered = React.useMemo(() => {
    if (tab !== "mine") return voices;
    const q = debouncedSearch.toLowerCase();
    if (!q) return voices;
    return voices.filter((v) =>
      `${v.name || ""} ${v.description || ""} ${v.category || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [debouncedSearch, tab, voices]);

  const onPlay = (voice) => {
    const url = voice?.preview_url;
    if (!url) return;
    if (playingId === voice.voice_id) {
      stopPreview();
      return;
    }
    stopPreview();
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingId(voice.voice_id);
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
  };

  const selectSx = {
    minWidth: 128,
    height: 36,
    borderRadius: 999,
    fontSize: "0.8125rem",
    fontWeight: 600,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: 1,
          borderColor: "divider",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          fontWeight: 800,
        }}
      >
        {lang?.videoStudioChooseVoiceTitle || "Choose a voice"}
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {lang?.videoStudioChooseVoiceHint ||
            "Pick a voice from My Voices or the library. It will speak the Dialogue in your prompt."}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Chip
            label={lang?.voicesTabExplore || "Explore"}
            onClick={() => setTab("explore")}
            color={tab === "explore" ? "primary" : "default"}
            variant={tab === "explore" ? "filled" : "outlined"}
          />
          <Chip
            label={lang?.voicesTabMine || "My Voices"}
            onClick={() => setTab("mine")}
            color={tab === "mine" ? "primary" : "default"}
            variant={tab === "mine" ? "filled" : "outlined"}
          />
        </Stack>

        {tab === "explore" ? (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5, alignItems: "center" }}
          >
            <FormControl size="small">
              <Select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <LanguageOutlined sx={{ fontSize: 16 }} />
                  </InputAdornment>
                }
                sx={selectSx}
              >
                {LANGUAGE_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "all"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <Select
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <RecordVoiceOverOutlined sx={{ fontSize: 16 }} />
                  </InputAdornment>
                }
                sx={selectSx}
              >
                {ACCENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "all"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {USE_CASE_CHIPS.map((c) => {
              const Icon = c.icon;
              const active = useCase === c.id;
              return (
                <Chip
                  key={c.id}
                  icon={<Icon sx={{ fontSize: 16 }} />}
                  label={c.label}
                  onClick={() => setUseCase((prev) => (prev === c.id ? "" : c.id))}
                  color={active ? "primary" : "default"}
                  variant={active ? "filled" : "outlined"}
                  sx={{ fontWeight: 600, height: 36 }}
                />
              );
            })}
          </Stack>
        ) : null}

        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            tab === "explore"
              ? lang?.voicesSearchLibrary || "Search library voices..."
              : lang?.voicesSearchPlaceholder || "Search voices..."
          }
          sx={{ mb: 1.5 }}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !filtered.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {lang?.voicesEmptyLibrary || "No voices match your filters."}
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
            {filtered.map((voice) => (
              <VoiceRow
                key={`${voice.public_owner_id || "local"}:${voice.voice_id}`}
                voice={voice}
                selected={selected?.voice_id === voice.voice_id}
                playing={playingId === voice.voice_id}
                onSelect={setSelected}
                onPlay={onPlay}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {lang?.cancel || "Cancel"}
        </Button>
        <Button
          variant="contained"
          disabled={!selected?.voice_id}
          onClick={() => {
            stopPreview();
            onConfirm?.(selected);
          }}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {lang?.videoStudioUseVoice || "Use voice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
